"use client";

import React, { useState, useMemo, useCallback } from "react";
import { useMailboxStore } from "@/vybe-trader-client/mailbox/stores/useMailboxStore";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import {
  type MailRow,
  type MailboxFilterType,
  DirectionIcon,
  SeverityMark,
  displayParty,
  filterMailRows,
  listPartyLabel,
  toMailRow,
} from "@/vybe-trader-client/mailbox/mailboxView";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn-components/ui/dropdown-menu";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { Textarea } from "@/shadcn-components/ui/textarea";
import { Filter, Mail, PenLine, Reply, Search, Trash2, X } from "lucide-react";
import { EmptyBlock } from "@/components/common/EmptyBlock";

type SortType = "date" | "subject";

export default function MailboxDesktopContent() {
  const { client } = useVybeTraderClient();
  const storeMessages = useMailboxStore((s) => s.messages);
  const activeAccountKey = useMailboxStore((s) => s.activeAccountKey);
  const [filterType, setFilterType] = useState<MailboxFilterType>("all");
  const [sortBy, setSortBy] = useState<SortType>("date");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [composeData, setComposeData] = useState({
    to: "Support",
    subject: "",
    message: "",
  });

  const rows = useMemo(
    () => storeMessages.map((m) => toMailRow(m, activeAccountKey)),
    [storeMessages, activeAccountKey]
  );

  const filteredRows = useMemo(() => {
    let list = filterMailRows(rows, filterType, searchQuery);
    if (sortBy === "subject") {
      list = [...list].sort((a, b) => a.subject.localeCompare(b.subject));
    }
    return list;
  }, [rows, filterType, sortBy, searchQuery]);

  const selected = useMemo(
    () => filteredRows.find((r) => r.id === selectedId) ?? null,
    [filteredRows, selectedId]
  );

  const unreadCount = useMemo(() => rows.filter((r) => r.isUnread).length, [rows]);

  const handleSelect = useCallback(
    (row: MailRow) => {
      setSelectedId(row.id);
      setComposing(false);
      void client.markMailboxMessageRead(row.id);
      useMailboxStore.getState().markUserRead(row.id);
    },
    [client]
  );

  const handleSendCompose = useCallback(async () => {
    if (!composeData.subject.trim() && !composeData.message.trim()) return;
    await client.sendMailboxMessage({
      to: composeData.to.trim() || "Support",
      subject: composeData.subject,
      body: composeData.message,
    });
    setComposing(false);
    setComposeData({ to: "Support", subject: "", message: "" });
  }, [client, composeData]);

  const handleReply = useCallback(() => {
    if (!selected) return;
    const replyTo = selected.from === "Support" ? "Support" : selected.from;
    setComposeData({
      to: replyTo,
      subject: selected.subject.startsWith("Re:")
        ? selected.subject
        : `Re: ${selected.subject}`,
      message: "",
    });
    setComposing(true);
  }, [selected]);

  const handleDelete = useCallback((id: string) => {
    useMailboxStore.getState().removeMessage(id);
    setSelectedId((cur) => (cur === id ? null : cur));
  }, []);

  const filterLabel =
    filterType === "all"
      ? "All"
      : filterType === "unread"
        ? "Unread"
        : "Support";

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Toolbar — matches Journal / History footer panels */}
      <div className="flex h-8 shrink-0 items-center gap-2 border-b border-border bg-muted/20 px-2">
        <span className="shrink-0 text-xs font-semibold">Mailbox</span>

        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-6 pl-7 text-[11px]"
          />
        </div>

        <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
          {filteredRows.length}
          {unreadCount > 0 ? ` · ${unreadCount} unread` : ""}
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-6 shrink-0 px-2 text-xs">
              <Filter className="mr-1 h-3 w-3" />
              {filterLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuLabel className="text-xs">Show</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={filterType === "all"}
              onCheckedChange={() => setFilterType("all")}
              className="text-xs"
            >
              All messages
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={filterType === "unread"}
              onCheckedChange={() => setFilterType("unread")}
              className="text-xs"
            >
              Unread only
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={filterType === "support"}
              onCheckedChange={() => setFilterType("support")}
              className="text-xs"
            >
              Support
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs">Sort</DropdownMenuLabel>
            <DropdownMenuCheckboxItem
              checked={sortBy === "date"}
              onCheckedChange={() => setSortBy("date")}
              className="text-xs"
            >
              Newest first
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={sortBy === "subject"}
              onCheckedChange={() => setSortBy("subject")}
              className="text-xs"
            >
              Subject A–Z
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="sm"
          className="h-6 shrink-0 px-2 text-xs"
          onClick={() => {
            setComposing(true);
            setSelectedId(null);
            setComposeData({ to: "Support", subject: "", message: "" });
          }}
        >
          <PenLine className="mr-1 h-3 w-3" />
          Compose
        </Button>
      </div>

      {/* Split list + detail */}
      <div className="flex min-h-0 flex-1">
        {/* Message list */}
        <div className="flex w-[46%] min-w-[160px] max-w-[300px] flex-col border-r border-border">
          <div
            className="min-h-0 flex-1 overflow-y-auto"
            role="listbox"
            aria-label="Mailbox messages"
            aria-activedescendant={selectedId ? `mailbox-item-${selectedId}` : undefined}
            data-roving-container
          >
            {filteredRows.length === 0 ? (
              <EmptyBlock
                config={{
                  glyph: "✉",
                  message:
                    searchQuery || filterType !== "all"
                      ? "No messages match your filters"
                      : "No messages",
                  action: {
                    label: "▷ compose a message",
                    onClick: () => {
                      setComposing(true);
                      setSelectedId(null);
                      setComposeData({ to: "Support", subject: "", message: "" });
                    },
                  },
                }}
              />
            ) : (
              filteredRows.map((row, index) => {
                const active = row.id === selectedId;
                const isRoving = active || (selectedId == null && index === 0);
                const moveFocus = (target: number) => {
                  const clamped = Math.max(0, Math.min(filteredRows.length - 1, target));
                  handleSelect(filteredRows[clamped]);
                  const el = document.querySelectorAll<HTMLElement>(
                    '[data-mailbox-item]',
                  )[clamped];
                  el?.focus();
                };
                return (
                  <button
                    key={row.id}
                    id={`mailbox-item-${row.id}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    data-mailbox-item
                    data-roving-item
                    tabIndex={isRoving ? 0 : -1}
                    onClick={() => handleSelect(row)}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        moveFocus(index + 1);
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        moveFocus(index - 1);
                      } else if (e.key === "Home") {
                        e.preventDefault();
                        moveFocus(0);
                      } else if (e.key === "End") {
                        e.preventDefault();
                        moveFocus(filteredRows.length - 1);
                      }
                    }}
                    className={`flex w-full items-center gap-1.5 border-b border-border px-2 py-1 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${
                      active ? "bg-muted/50" : row.isUnread ? "bg-muted/20" : ""
                    }`}
                  >
                    {row.isUnread ? (
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    ) : (
                      <span className="w-1.5 shrink-0" />
                    )}
                    <DirectionIcon isSent={row.isSent} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <span
                          className={`truncate text-[11px] ${
                            row.isUnread ? "font-semibold text-foreground" : "text-foreground/90"
                          }`}
                        >
                          {listPartyLabel(row)}
                        </span>
                        <SeverityMark severity={row.severity} />
                      </div>
                      <span
                        className={`block truncate text-[10px] ${
                          row.isUnread ? "text-foreground/80" : "text-muted-foreground"
                        }`}
                      >
                        {row.subject}
                      </span>
                    </div>
                    <div className="flex shrink-0 flex-col items-end text-[10px] leading-tight tabular-nums text-muted-foreground">
                      <span>{row.date}</span>
                      <span>{row.time}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Detail / compose */}
        <div className="flex min-w-0 flex-1 flex-col">
          {composing ? (
            <>
              <div className="flex h-7 shrink-0 items-center justify-between border-b border-border px-2">
                <span className="text-xs font-semibold">New message</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0"
                  onClick={() => setComposing(false)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
                <div className="flex items-center gap-2">
                  <span className="w-12 shrink-0 text-[10px] text-muted-foreground">To</span>
                  <Input
                    value={composeData.to}
                    onChange={(e) =>
                      setComposeData((p) => ({ ...p, to: e.target.value }))
                    }
                    className="h-6 flex-1 text-[11px]"
                    placeholder="Support"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-12 shrink-0 text-[10px] text-muted-foreground">
                    Subject
                  </span>
                  <Input
                    value={composeData.subject}
                    onChange={(e) =>
                      setComposeData((p) => ({ ...p, subject: e.target.value }))
                    }
                    className="h-6 flex-1 text-[11px]"
                    placeholder="Subject"
                  />
                </div>
                <Textarea
                  value={composeData.message}
                  onChange={(e) =>
                    setComposeData((p) => ({ ...p, message: e.target.value }))
                  }
                  placeholder="Message…"
                  className="min-h-[80px] flex-1 resize-none text-[11px]"
                  rows={6}
                />
                <div className="flex justify-end gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => setComposing(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => void handleSendCompose()}
                  >
                    Send
                  </Button>
                </div>
              </div>
            </>
          ) : selected ? (
            <>
              <div className="flex h-7 shrink-0 items-center justify-between gap-2 border-b border-border px-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold">{selected.subject}</p>
                  <p className="flex items-center gap-1 truncate text-[10px] text-muted-foreground">
                    <DirectionIcon isSent={selected.isSent} />
                    <span>
                      {selected.isSent
                        ? `Sent to ${displayParty(selected.to)}`
                        : `From ${displayParty(selected.from)}`}
                      {" · "}
                      {selected.date} {selected.time}
                    </span>
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5 text-xs"
                    onClick={handleReply}
                    title="Reply"
                  >
                    <Reply className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5 text-xs text-destructive hover:text-destructive"
                    onClick={() => handleDelete(selected.id)}
                    title="Remove from list"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-2">
                <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-foreground/90">
                  {selected.body}
                </p>
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-1 text-center">
              <Mail className="h-5 w-5 text-muted-foreground/40" />
              <p className="text-[11px] text-muted-foreground">
                Select a message or compose
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
